import React, { useEffect, useState } from 'react';
import {
  Card, Form, Input, Button, Typography, Alert, Avatar, Space, message,
  Tabs, Table, Tag, Modal, Popconfirm, Spin, Divider, InputNumber, Tooltip,
  theme as antdTheme, List as AntList, Select, Row, Col, Empty,
} from 'antd';
import {
  UserOutlined, LockOutlined, MailOutlined, SaveOutlined,
  PlusOutlined, DeleteOutlined, CheckCircleOutlined,
  CloudOutlined, ApiOutlined, ReloadOutlined, InfoCircleOutlined,
  UserAddOutlined, KeyOutlined, SafetyCertificateOutlined, RocketOutlined, GlobalOutlined,
  ClockCircleOutlined, ExclamationCircleOutlined, SyncOutlined,
  CrownOutlined, CreditCardOutlined, FileTextOutlined, BankOutlined,
  EyeOutlined, LinkOutlined, StopOutlined, ThunderboltOutlined,
  CloudServerOutlined, EditOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { PRO_PRICE_MONTH } from '../utils/pricing';

const { Text } = Typography;

// ─────────────────────────────────────────────
// Profile + Password tab
// ─────────────────────────────────────────────
const ProfileTab = () => {
  const { token: themeToken } = antdTheme.useToken(); // used for Input prefix icon color only
  const [profileForm]    = Form.useForm();
  const [passwordForm]   = Form.useForm();
  const [profileLoading, setProfileLoading]   = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profileError,   setProfileError]     = useState('');
  const [passwordError,  setPasswordError]    = useState('');
  const [user, setUser]                       = useState(null);

  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get('/api/auth/me', { headers })
      .then(res => {
        setUser(res.data);
        profileForm.setFieldsValue({ name: res.data.name, email: res.data.email });
      })
      .catch(() => {});
  }, []);

  const handleProfileSave = async values => {
    setProfileLoading(true);
    setProfileError('');
    try {
      const res = await axios.put('/api/auth/me', { name: values.name }, { headers });
      setUser(res.data);
      localStorage.setItem('userName', res.data.name || '');
      message.success('Profile updated');
    } catch (err) {
      setProfileError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSave = async values => {
    setPasswordLoading(true);
    setPasswordError('');
    try {
      await axios.put('/api/auth/me', { currentPassword: values.currentPassword, newPassword: values.newPassword }, { headers });
      message.success('Password changed successfully');
      passwordForm.resetFields();
    } catch (err) {
      setPasswordError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const initials = user?.name
    ? user.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : user?.email?.[0]?.toUpperCase() || '?';

  return (
    <div style={{ maxWidth: 600, width: '100%' }}>
      {/* Profile card */}
      <Card style={{ borderRadius: 'var(--r-lg)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
          <Avatar size={56} style={{ background: 'linear-gradient(135deg,var(--brand-primary),#2e6bf5)', fontSize: 20, fontWeight: 700 }}>
            {initials}
          </Avatar>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>{user?.name || user?.email || '—'}</div>
            <div style={{ color: 'var(--text-2)', fontSize: 13 }}>{user?.email}</div>
          </div>
        </div>

        {profileError && <Alert message={profileError} type="error" showIcon closable onClose={() => setProfileError('')} style={{ marginBottom: 16, borderRadius: 'var(--r-md)' }} />}

        <Form form={profileForm} layout="vertical" onFinish={handleProfileSave}>
          <Form.Item name="name" label="Full Name">
            <Input prefix={<UserOutlined style={{ color: themeToken.colorTextTertiary }} />} placeholder="Your name" style={{ borderRadius: 'var(--r-md)' }} />
          </Form.Item>
          <Form.Item name="email" label="Email Address">
            <Input prefix={<MailOutlined style={{ color: themeToken.colorTextTertiary }} />} disabled style={{ borderRadius: 'var(--r-md)' }} />
          </Form.Item>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={profileLoading} style={{ borderRadius: 'var(--r-md)' }}>
            Save Changes
          </Button>
        </Form>
      </Card>

      {/* Password card */}
      <Card style={{ borderRadius: 'var(--r-lg)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
        <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 20 }}>Change Password</Text>

        {passwordError && <Alert message={passwordError} type="error" showIcon closable onClose={() => setPasswordError('')} style={{ marginBottom: 16, borderRadius: 'var(--r-md)' }} />}

        <Form form={passwordForm} layout="vertical" onFinish={handlePasswordSave}>
          <Form.Item name="currentPassword" label="Current Password" rules={[{ required: true, message: 'Required' }]}>
            <Input.Password prefix={<LockOutlined style={{ color: themeToken.colorTextTertiary }} />} placeholder="••••••••" style={{ borderRadius: 'var(--r-md)' }} />
          </Form.Item>
          <Form.Item name="newPassword" label="New Password" rules={[{ required: true, min: 8, message: 'Min 8 characters' }]}>
            <Input.Password prefix={<LockOutlined style={{ color: themeToken.colorTextTertiary }} />} placeholder="Min 8 characters" style={{ borderRadius: 'var(--r-md)' }} />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirm New Password"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Please confirm' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined style={{ color: themeToken.colorTextTertiary }} />} placeholder="Repeat new password" style={{ borderRadius: 'var(--r-md)' }} />
          </Form.Item>
          <Button type="primary" htmlType="submit" icon={<LockOutlined />} loading={passwordLoading} style={{ borderRadius: 'var(--r-md)' }}>
            Change Password
          </Button>
        </Form>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────
// Role config
// ─────────────────────────────────────────────
const ROLE_CONFIG = {
  owner:   { label: 'Owner',   color: 'var(--brand-primary)', bg: 'var(--brand-tint)', rawColor: '#0a40d9', desc: 'Full control of the organisation' },
  admin:   { label: 'Admin',   color: 'var(--ok)',            bg: 'var(--ok-bg)',      rawColor: '#10b981', desc: 'Manage everything except billing' },
  manager: { label: 'Manager', color: 'var(--warn)',          bg: 'var(--warn-bg)',    rawColor: '#f59e0b', desc: 'Manage campaigns, subscribers & lists' },
  viewer:  { label: 'Viewer',  color: 'var(--text-2)',        bg: 'var(--surface-2)', rawColor: '#6b7280', desc: 'Read-only access' },
};

const RoleTag = ({ role }) => {
  const cfg = ROLE_CONFIG[role] || ROLE_CONFIG.viewer;
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.rawColor}33`,
      borderRadius: 20, fontWeight: 600, fontSize: 11,
      padding: '2px 10px', display: 'inline-block',
    }}>
      {cfg.label}
    </span>
  );
};

// Decode plan from JWT without a library
const getMyPlan = () => {
  try {
    const tok = localStorage.getItem('token');
    if (!tok) return 'free';
    return JSON.parse(atob(tok.split('.')[1])).plan || 'free';
  } catch { return 'free'; }
};

const getMyRole = () => {
  try {
    const tok = localStorage.getItem('token');
    if (!tok) return 'viewer';
    return JSON.parse(atob(tok.split('.')[1])).role || 'viewer';
  } catch { return 'viewer'; }
};

// ─────────────────────────────────────────────
// Users tab
// ─────────────────────────────────────────────
const UsersTab = () => {
  const { token: themeToken } = antdTheme.useToken(); // kept for Input prefix icon color
  const [users,        setUsers]        = useState([]);
  const [seatInfo,     setSeatInfo]     = useState(null); // { seats_used, seats_allowed, paid_seats }
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [modalOpen,    setModalOpen]    = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [updatingRole, setUpdatingRole] = useState(null);
  const [buyingSeat,   setBuyingSeat]   = useState(false);
  const [removingSeat, setRemovingSeat] = useState(false);
  const [seatModal,    setSeatModal]    = useState(false);
  const [form]                         = Form.useForm();

  // Region-aware seat pricing (matches the Pro plan currency logic)
  const IS_INDIA    = (() => { try { const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; return tz === 'Asia/Kolkata' || tz === 'Asia/Calcutta'; } catch { return false; } })();
  const SEAT_CCY    = IS_INDIA ? 'INR' : 'USD';
  const SEAT_PRICE  = IS_INDIA ? '₹199' : '$1.99';

  const token    = localStorage.getItem('token');
  const headers  = { Authorization: `Bearer ${token}` };
  const myEmail  = localStorage.getItem('userEmail') || '';
  const myPlan   = getMyPlan();
  const myRole   = getMyRole();
  const canManage = ['owner', 'admin'].includes(myRole);
  const isPro     = myPlan !== 'free';

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const [usersRes, billingRes] = await Promise.all([
        axios.get('/api/settings/users', { headers }),
        axios.get('/api/billing/status', { headers }),
      ]);
      setUsers(usersRes.data);
      setSeatInfo({
        seats_used:    billingRes.data.seats_used    ?? usersRes.data.length,
        seats_allowed: billingRes.data.seats_allowed ?? 1,
        paid_seats:    billingRes.data.paid_seats    ?? 0,
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // Show success toast when returning from Stripe seat checkout
    if (window.location.search.includes('seat_purchased=1')) {
      message.success('Seat purchased! You can now add a new team member.', 5);
      window.history.replaceState({}, '', window.location.pathname + window.location.hash);
    }
  }, []); // eslint-disable-line

  const handleCreate = async values => {
    setSaving(true);
    try {
      await axios.post('/api/settings/users', values, { headers });
      message.success(`User ${values.email} added`);
      form.resetFields();
      setModalOpen(false);
      fetchUsers();
    } catch (err) {
      const d = err.response?.data || {};
      if (d.needs_seat) {
        // Close add modal, open buy-seat modal
        setModalOpen(false);
        setSeatModal(true);
      } else {
        message.error(d.message || 'Failed to add user');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBuySeat = async () => {
    setBuyingSeat(true);
    try {
      const res = await axios.post('/api/billing/buy-seat', { currency: SEAT_CCY }, { headers });
      message.success(res.data.message || 'Seat added');
      setSeatModal(false);
      fetchUsers();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to add seat');
    } finally {
      setBuyingSeat(false);
    }
  };

  const handleRemoveSeat = async () => {
    setRemovingSeat(true);
    try {
      const res = await axios.post('/api/billing/remove-seat', {}, { headers });
      message.success(res.data.message || 'Seat removed');
      fetchUsers();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to remove seat');
    } finally {
      setRemovingSeat(false);
    }
  };

  const handleRoleChange = async (id, role) => {
    setUpdatingRole(id);
    try {
      await axios.put(`/api/settings/users/${id}/role`, { role }, { headers });
      setUsers(prev => prev.map(u => u.id === id ? { ...u, role } : u));
      message.success('Role updated');
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to update role');
    } finally {
      setUpdatingRole(null);
    }
  };

  const handleDelete = async id => {
    try {
      await axios.delete(`/api/settings/users/${id}`, { headers });
      message.success('User deleted');
      fetchUsers();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const columns = [
    {
      title: 'User', key: 'user',
      render: (_, r) => (
        <Space>
          <Avatar size={34} style={{ background: 'linear-gradient(135deg,var(--brand-primary),#2e6bf5)', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
            {r.name ? r.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : r.email[0].toUpperCase()}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>
              {r.name || '—'}
              {r.email === myEmail && (
                <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, background: 'var(--info-bg)', color: 'var(--info)', border: '1px solid var(--info)', borderRadius: 20, padding: '1px 7px', display: 'inline-block' }}>You</span>
              )}
            </div>
            <div style={{ color: 'var(--text-2)', fontSize: 12 }}>{r.email}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Role', dataIndex: 'role', width: 180,
      render: (role, r) => {
        const isMe = r.email === myEmail;
        const isOwner = role === 'owner';
        // Owner role and own row can't be changed
        if (isMe || isOwner || !canManage) return <RoleTag role={role} />;
        return (
          <Select
            value={role}
            size="small"
            loading={updatingRole === r.id}
            onChange={val => handleRoleChange(r.id, val)}
            style={{ width: 130 }}
            options={[
              { value: 'admin',   label: <Space size={4}><span style={{ color: ROLE_CONFIG.admin.color }}>●</span>Admin</Space> },
              { value: 'manager', label: <Space size={4}><span style={{ color: ROLE_CONFIG.manager.color }}>●</span>Manager</Space> },
              { value: 'viewer',  label: <Space size={4}><span style={{ color: ROLE_CONFIG.viewer.color }}>●</span>Viewer</Space> },
            ]}
          />
        );
      },
    },
    {
      title: 'Added', dataIndex: 'created_at', width: 130, responsive: ['md'],
      render: d => <Text style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Text>,
    },
    {
      title: '', key: 'actions', width: 50, align: 'right',
      render: (_, r) => {
        if (r.email === myEmail || r.role === 'owner' || !canManage) return null;
        return (
          <Popconfirm title={`Remove ${r.name || r.email}?`} description="They will lose access immediately." onConfirm={() => handleDelete(r.id)} okText="Remove" okButtonProps={{ danger: true }}>
            <Button danger type="text" icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        );
      },
    },
  ];

  // ── Free plan wall ─────────────────────────────────────────────────────
  if (!isPro) {
    return (
      <div style={{ maxWidth: 720, width: '100%' }}>
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ fontSize: 15 }}>Team Members</Text>
          <div style={{ color: 'var(--text-2)', fontSize: 13 }}>Invite your team to collaborate on campaigns</div>
        </div>
        <Card style={{ borderRadius: 'var(--r-lg)', border: '1px solid var(--border)', background: 'var(--brand-tint)', textAlign: 'center', padding: '40px 24px' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>👥</div>
          <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 8, color: 'var(--text)' }}>Team Members is a Pro feature</Text>
          <Text style={{ color: 'var(--text-2)', display: 'block', marginBottom: 20, maxWidth: 380, margin: '0 auto 20px' }}>
            Upgrade to Pro to add team members with roles like Admin, Manager, and Viewer — and collaborate on campaigns together.
          </Text>
          <Button
            type="primary"
            size="large"
            icon={<RocketOutlined />}
            onClick={() => window.location.href = '/billing'}
            style={{ borderRadius: 'var(--r-md)', fontWeight: 600 }}
          >
            Upgrade to Pro — {PRO_PRICE_MONTH}
          </Button>
          <div style={{ marginTop: 12 }}>
            <Text style={{ fontSize: 12, color: 'var(--text-3)' }}>Unlimited team members · Role-based access · Cancel anytime</Text>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720, width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Text strong style={{ fontSize: 15, color: 'var(--text)' }}>Team Members</Text>
          <div style={{ color: 'var(--text-2)', fontSize: 13, marginTop: 2, fontFamily: 'var(--font-mono)' }}>
            {seatInfo ? (
              <>
                <span style={{ fontWeight: 600, color: seatInfo.seats_used >= seatInfo.seats_allowed ? 'var(--danger)' : 'var(--text)' }}>
                  {seatInfo.seats_used}
                </span>
                {' / '}
                <span style={{ fontWeight: 600 }}>{seatInfo.seats_allowed}</span>
                <span style={{ fontFamily: 'inherit' }}>{' seats used'}</span>
                {seatInfo.paid_seats > 0 && (
                  <span style={{ marginLeft: 6, color: 'var(--brand-primary)', fontFamily: 'inherit' }}>
                    · {seatInfo.paid_seats} additional seat{seatInfo.paid_seats !== 1 ? 's' : ''} @ {SEAT_PRICE}/mo each
                  </span>
                )}
              </>
            ) : 'Role-based access control'}
          </div>
        </div>
        {canManage && (
          <Space wrap>
            {seatInfo?.paid_seats > 0 && (
              <Popconfirm
                title="Remove a paid seat?"
                description="Your next invoice will be reduced. You can't remove a seat that's in use."
                okText="Remove seat" cancelText="Keep"
                onConfirm={handleRemoveSeat}
              >
                <Button danger loading={removingSeat} style={{ borderRadius: 'var(--r-md)' }}>
                  Remove Seat
                </Button>
              </Popconfirm>
            )}
            <Button
              icon={<PlusOutlined />}
              onClick={() => setSeatModal(true)}
              style={{ borderRadius: 'var(--r-md)' }}
            >
              Buy Seat — {SEAT_PRICE}/mo
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)} style={{ borderRadius: 'var(--r-md)', fontWeight: 600 }}>
              Add Member
            </Button>
          </Space>
        )}
      </div>

      {/* Role legend */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {Object.entries(ROLE_CONFIG).map(([key, cfg]) => (
          <Tooltip key={key} title={cfg.desc}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.rawColor}33`, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600, cursor: 'default' }}>
              {cfg.label}
            </span>
          </Tooltip>
        ))}
      </div>

      {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} style={{ marginBottom: 16, borderRadius: 'var(--r-md)' }} />}

      <Card style={{ borderRadius: 'var(--r-lg)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
        <Table
          dataSource={users}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* ── Buy Seat Modal ── */}
      <Modal
        title={<Space><div style={{ width: 32, height: 32, background: 'var(--warn-bg)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><UserAddOutlined style={{ color: 'var(--warn)' }} /></div><span>Purchase a Seat</span></Space>}
        open={seatModal}
        onCancel={() => setSeatModal(false)}
        footer={null}
        destroyOnHidden
        width={440}
      >
        <div style={{ padding: '16px 0' }}>
          <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', padding: '20px 20px', marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text strong>Additional Team Member Seat</Text>
              <Text strong style={{ fontSize: 18, color: 'var(--brand-primary)', fontFamily: 'var(--font-mono)' }}>{SEAT_PRICE}<Text style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400 }}>/month</Text></Text>
            </div>
            <ul style={{ color: 'var(--text-2)', fontSize: 13, paddingLeft: 20, margin: 0, lineHeight: 2 }}>
              <li>One additional login for your team</li>
              <li>Assign role: Admin, Manager, or Viewer</li>
              <li>Cancel or remove the seat anytime</li>
              <li>Billed monthly alongside your Pro plan</li>
            </ul>
          </div>
          {seatInfo && (
            <div style={{ background: 'var(--brand-tint)', borderRadius: 'var(--r-md)', padding: '10px 16px', marginBottom: 20, fontSize: 13, color: 'var(--brand-primary)', fontFamily: 'var(--font-mono)' }}>
              Currently using <strong>{seatInfo.seats_used}</strong> of <strong>{seatInfo.seats_allowed}</strong> available seat{seatInfo.seats_allowed !== 1 ? 's' : ''}.
              <span style={{ fontFamily: 'inherit' }}> The seat is available right away; {SEAT_PRICE}/mo is added to your next invoice and billed monthly.</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setSeatModal(false)} style={{ borderRadius: 'var(--r-md)' }}>Cancel</Button>
            <Button
              type="primary"
              icon={<RocketOutlined />}
              loading={buyingSeat}
              onClick={handleBuySeat}
              style={{ borderRadius: 'var(--r-md)', fontWeight: 600 }}
            >
              Buy Seat — {SEAT_PRICE}/month
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Add Member Modal ── */}
      <Modal
        title={<Space><div style={{ width: 32, height: 32, background: 'var(--brand-tint)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><UserOutlined style={{ color: 'var(--brand-primary)' }} /></div><span>Add Team Member</span></Space>}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        footer={null}
        destroyOnHidden
        width={460}
      >
        <Form form={form} layout="vertical" onFinish={handleCreate} style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Full Name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input prefix={<UserOutlined style={{ color: themeToken.colorTextTertiary }} />} placeholder="e.g. Jane Smith" style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item name="email" label="Email Address" rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}>
            <Input prefix={<MailOutlined style={{ color: themeToken.colorTextTertiary }} />} placeholder="jane@example.com" style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item name="role" label="Role" initialValue="viewer" rules={[{ required: true }]}>
            <Select style={{ borderRadius: 10 }}>
              {Object.entries(ROLE_CONFIG).filter(([k]) => k !== 'owner').map(([key, cfg]) => (
                <Option key={key} value={key}>
                  <Space>
                    <span style={{ color: cfg.color, fontWeight: 700 }}>●</span>
                    <span style={{ fontWeight: 600 }}>{cfg.label}</span>
                    <Text style={{ color: themeToken.colorTextTertiary, fontSize: 12 }}>— {cfg.desc}</Text>
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="password" label="Password" rules={[{ required: true, min: 8, message: 'Min 8 characters' }]}>
            <Input.Password prefix={<LockOutlined style={{ color: themeToken.colorTextTertiary }} />} placeholder="Min 8 characters" style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item
            name="confirm"
            label="Confirm Password"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) return Promise.resolve();
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined style={{ color: themeToken.colorTextTertiary }} />} placeholder="Repeat password" style={{ borderRadius: 10 }} />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <Button onClick={() => { setModalOpen(false); form.resetFields(); }} style={{ borderRadius: 8 }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={saving} style={{ borderRadius: 8 }}>Add Member</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

// ─────────────────────────────────────────────
// SES Setup Guide Modal
// ─────────────────────────────────────────────
const SES_STEPS = [
  {
    title: 'Create an IAM User',
    color: '#0a40d9', bg: '#e1e9ff',
    icon: <UserAddOutlined />,
    steps: [
      'Go to AWS Console → IAM → Users → Create user',
      'Give it a name like "ses-mailbox-ses"',
      'Attach the policy: AmazonSESFullAccess',
      'Complete creation — do NOT close the next screen',
    ],
  },
  {
    title: 'Generate Access Keys',
    color: '#f59e0b', bg: '#fef3c7',
    icon: <KeyOutlined />,
    steps: [
      'In the IAM user detail page, click "Security credentials" tab',
      'Scroll to "Access keys" → Create access key',
      'Choose "Application running outside AWS"',
      'Copy both the Access Key ID and Secret Access Key — the secret is shown only once',
      'Paste both values into the form on this page',
    ],
  },
  {
    title: 'Verify Your Sender Email',
    color: '#10b981', bg: '#d1fae5',
    icon: <SafetyCertificateOutlined />,
    steps: [
      'Go to AWS Console → Amazon SES → Verified Identities',
      'Click "Create identity" → choose Email address or Domain',
      'Enter the email you want to send from (e.g. noreply@yourdomain.com)',
      'AWS will send a verification email — click the link inside it',
      'Status will change to "Verified" once confirmed',
    ],
  },
  {
    title: 'Exit the SES Sandbox (Production Access)',
    color: '#3b82f6', bg: '#dbeafe',
    icon: <RocketOutlined />,
    steps: [
      'By default SES accounts are in sandbox — you can only send to verified emails',
      'To send to any email, go to SES → Account Dashboard → Request production access',
      'Fill in the use-case form — approval usually takes 24 hours',
      'Until approved, verify each recipient email address in Verified Identities',
    ],
  },
  {
    title: 'Set Your Region',
    color: '#2e6bf5', bg: '#eef3ff',
    icon: <GlobalOutlined />,
    steps: [
      'Use the same AWS region where your SES identity is verified',
      'Common choices: us-east-1 (N. Virginia), eu-west-1 (Ireland), ap-south-1 (Mumbai)',
      'You can check your active region in the top-right corner of the AWS Console',
    ],
  },
];

const SesGuideModal = ({ open, onClose }) => (
  <Modal
    open={open}
    onCancel={onClose}
    footer={<Button type="primary" onClick={onClose} style={{ borderRadius: 'var(--r-md)' }}>Got it</Button>}
    width={640}
    title={
      <Space>
        <div style={{ width: 32, height: 32, background: 'var(--warn-bg)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CloudOutlined style={{ color: 'var(--warn)' }} />
        </div>
        <span>AWS SES Setup Guide</span>
      </Space>
    }
    destroyOnHidden
    styles={{ body: { maxHeight: '65vh', overflowY: 'auto', paddingTop: 8 } }}
  >
    <Text style={{ color: 'var(--text-2)', fontSize: 13, display: 'block', marginBottom: 20 }}>
      Follow these steps to connect your AWS SES account and start sending emails.
    </Text>

    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {SES_STEPS.map((section, i) => (
        <div key={i} style={{ borderRadius: 'var(--r-md)', border: `1px solid ${section.bg}`, overflow: 'hidden' }}>
          <div style={{ background: section.bg, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%',
              background: section.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 14, flexShrink: 0,
            }}>
              {section.icon}
            </div>
            <Text strong style={{ color: section.color, fontSize: 14 }}>
              Step {i + 1}: {section.title}
            </Text>
          </div>
          <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {section.steps.map((s, j) => (
              <div key={j} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{
                  minWidth: 20, height: 20, borderRadius: '50%',
                  background: section.bg, border: `1.5px solid ${section.color}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, color: section.color, marginTop: 1,
                }}>
                  {j + 1}
                </div>
                <Text style={{ fontSize: 13, color: 'var(--text)', lineHeight: '20px' }}>{s}</Text>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>

    <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--surface-2)', borderRadius: 'var(--r-md)', border: '1px solid var(--border)' }}>
      <Text style={{ fontSize: 12, color: 'var(--text-2)' }}>
        💡 <strong>Tip:</strong> After saving your credentials, use the <strong>Test Connection</strong> button to verify everything is working. It will show your daily send quota and current sending rate.
      </Text>
    </div>
  </Modal>
);

// ─────────────────────────────────────────────
// ─────────────────────────────────────────────
// From-Email addresses sub-component
// ─────────────────────────────────────────────
const FromEmailsCard = ({ themeToken }) => {
  const [fromEmails,    setFromEmails]    = useState([]);
  const [feLoading,     setFeLoading]     = useState(true);
  const [addModal,      setAddModal]      = useState(false);
  const [addEmail,      setAddEmail]      = useState('');
  const [addName,       setAddName]       = useState('');
  const [adding,        setAdding]        = useState(false);
  const [refreshing,    setRefreshing]    = useState(false);

  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadEmails = async (silent = false) => {
    if (!silent) setFeLoading(true);
    try {
      const res = await axios.get('/api/settings/ses/from-emails', { headers });
      setFromEmails(res.data || []);
    } catch { /* non-fatal */ }
    finally { setFeLoading(false); setRefreshing(false); }
  };

  useEffect(() => { loadEmails(); }, []);

  const handleAdd = async () => {
    if (!addEmail) return;
    setAdding(true);
    try {
      const res = await axios.post('/api/settings/ses/from-emails', { email: addEmail, from_name: addName }, { headers });
      message.success(res.data.message || 'Verification email sent');
      setAddModal(false); setAddEmail(''); setAddName('');
      loadEmails(true);
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to add email');
    } finally { setAdding(false); }
  };

  const handleSetDefault = async (id) => {
    try {
      await axios.put(`/api/settings/ses/from-emails/${id}/default`, {}, { headers });
      message.success('Default sender updated');
      loadEmails(true);
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to update default');
    }
  };

  const handleResend = async (id, email) => {
    try {
      await axios.post(`/api/settings/ses/from-emails/${id}/resend-verification`, {}, { headers });
      message.success(`Verification email re-sent to ${email}`);
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to resend verification');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/settings/ses/from-emails/${id}`, { headers });
      message.success('Email address removed');
      loadEmails(true);
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to remove email');
    }
  };

  const statusBadge = (status) => {
    const cfg = {
      Success:    { color: 'var(--ok)',     bg: 'var(--ok-bg)',     label: 'Verified' },
      Pending:    { color: 'var(--warn)',   bg: 'var(--warn-bg)',   label: 'Pending' },
      Failed:     { color: 'var(--danger)', bg: 'var(--danger-bg)', label: 'Failed' },
      Unknown:    { color: 'var(--text-3)', bg: 'var(--surface-2)', label: 'Unknown' },
    }[status] || { color: 'var(--text-3)', bg: 'var(--surface-2)', label: status };
    return (
      <span style={{
        fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
        background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}`,
        opacity: 0.9,
      }}>
        {cfg.label}
      </span>
    );
  };

  return (
    <Card
      style={{ borderRadius: 'var(--r-lg)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', marginBottom: 16 }}
      styles={{ body: { padding: '20px 24px' } }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <Text strong style={{ fontSize: 15, display: 'block', color: 'var(--text)' }}>Verified From-Email Addresses</Text>
          <Text style={{ fontSize: 12, color: 'var(--text-2)' }}>
            Add multiple sender addresses. Each must be verified in AWS SES. Use them when creating campaigns.
          </Text>
        </div>
        <Space>
          <Button
            size="small" icon={<ReloadOutlined spin={refreshing} />}
            onClick={() => { setRefreshing(true); loadEmails(true); }}
            style={{ borderRadius: 'var(--r-md)' }}
          >
            Refresh
          </Button>
          <Button
            type="primary" size="small" icon={<PlusOutlined />}
            onClick={() => setAddModal(true)}
            style={{ borderRadius: 'var(--r-md)' }}
          >
            Add Email
          </Button>
        </Space>
      </div>

      {feLoading ? (
        <div style={{ textAlign: 'center', padding: '24px 0' }}><Spin /></div>
      ) : fromEmails.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-2)', fontSize: 13 }}>
          No from-email addresses added yet. Click <strong>Add Email</strong> to add your first sender address.
        </div>
      ) : (
        <AntList
          dataSource={fromEmails}
          renderItem={item => (
            <AntList.Item
              style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}
              actions={[
                !item.is_default && item.status === 'Success' && (
                  <Button
                    key="default" size="small" type="link"
                    style={{ padding: 0, color: 'var(--brand-primary)', fontSize: 12 }}
                    onClick={() => handleSetDefault(item.id)}
                  >
                    Set Default
                  </Button>
                ),
                item.status !== 'Success' && (
                  <Button
                    key="resend" size="small" type="link"
                    style={{ padding: 0, color: 'var(--warn)', fontSize: 12 }}
                    onClick={() => handleResend(item.id, item.email)}
                  >
                    Resend Verification
                  </Button>
                ),
                <Popconfirm
                  key="del"
                  title={`Remove ${item.email}?`}
                  description="This email will no longer be available as a sender."
                  onConfirm={() => handleDelete(item.id)}
                  okText="Remove"
                  okButtonProps={{ danger: true }}
                >
                  <Button size="small" type="link" danger style={{ padding: 0, fontSize: 12 }}>Remove</Button>
                </Popconfirm>
              ].filter(Boolean)}
            >
              <AntList.Item.Meta
                title={
                  <Space size={6}>
                    <Text strong style={{ fontSize: 13 }}>{item.from_name ? `${item.from_name} <${item.email}>` : item.email}</Text>
                    {statusBadge(item.status)}
                    {item.is_default && (
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                        background: 'var(--brand-tint)', color: 'var(--brand-primary)', border: '1px solid var(--brand-primary)',
                        opacity: 0.8,
                      }}>
                        Default
                      </span>
                    )}
                  </Space>
                }
              />
            </AntList.Item>
          )}
        />
      )}

      <Modal
        title="Add From-Email Address"
        open={addModal}
        onCancel={() => { setAddModal(false); setAddEmail(''); setAddName(''); }}
        onOk={handleAdd}
        okText="Add & Send Verification"
        confirmLoading={adding}
        okButtonProps={{ disabled: !addEmail }}
        destroyOnHidden
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 8 }}>
          <div>
            <Text style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Email Address *</Text>
            <Input
              type="email"
              placeholder="sender@yourdomain.com"
              value={addEmail}
              onChange={e => setAddEmail(e.target.value)}
              style={{ borderRadius: 8 }}
            />
            <Text style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4, display: 'block' }}>
              AWS will send a verification email to this address. You must click the link inside to verify it.
            </Text>
          </div>
          <div>
            <Text style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Display Name (optional)</Text>
            <Input
              placeholder="e.g., Acme Marketing"
              value={addName}
              onChange={e => setAddName(e.target.value)}
              style={{ borderRadius: 8 }}
            />
          </div>
        </div>
      </Modal>
    </Card>
  );
};

// SES / Email Config tab
// ─────────────────────────────────────────────
const SesTab = () => {
  const { token: themeToken } = antdTheme.useToken(); // kept for Input prefix icon colors
  const [form]          = Form.useForm();
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [testing,  setTesting]  = useState(false);
  const [error,    setError]    = useState('');
  const [testResult,     setTestResult]     = useState(null);
  const [identityStatus, setIdentityStatus] = useState(null); // { configured, email, status }
  const [identityLoading, setIdentityLoading] = useState(false);
  const [verifying,      setVerifying]      = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadIdentityStatus = async () => {
    setIdentityLoading(true);
    try {
      const res = await axios.get('/api/settings/ses/identity-status', { headers });
      setIdentityStatus(res.data);
    } catch { /* non-fatal */ }
    finally { setIdentityLoading(false); }
  };

  useEffect(() => {
    axios.get('/api/settings/ses', { headers })
      .then(res => {
        form.setFieldsValue({
          ses_access_key_id:     res.data.ses_access_key_id     || '',
          ses_secret_access_key: res.data.ses_secret_access_key || '',
          ses_region:            res.data.ses_region            || 'us-east-1',
          ses_from_email:        res.data.ses_from_email        || '',
          ses_from_name:         res.data.ses_from_name         || '',
          // ses_send_rate is admin-only — not shown in user settings
        });
      })
      .catch(() => {})
      .finally(() => { setLoading(false); loadIdentityStatus(); });
  }, []);

  const handleSave = async values => {
    setSaving(true);
    setError('');
    setTestResult(null);
    try {
      await axios.put('/api/settings/ses', values, { headers });
      message.success('SES settings saved');
      loadIdentityStatus();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    setError('');
    try {
      // Save current form values first so the test uses latest input
      const values = form.getFieldsValue();
      await axios.put('/api/settings/ses', values, { headers });
      const res = await axios.post('/api/settings/ses/test', {}, { headers });
      setTestResult({ ok: true, message: res.data.message, quota: res.data.quota, region: res.data.region });
      loadIdentityStatus();
    } catch (err) {
      setTestResult({ ok: false, message: err.response?.data?.message || 'Connection failed' });
    } finally {
      setTesting(false);
    }
  };

  const handleVerifyIdentity = async () => {
    setVerifying(true);
    try {
      const res = await axios.post('/api/settings/ses/verify-identity', {}, { headers });
      message.success(res.data.message, 8);
      setIdentityStatus(prev => ({ ...prev, status: 'Pending' }));
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to send verification email');
    } finally {
      setVerifying(false);
    }
  };

  const AWS_REGIONS = [
    'us-east-1','us-east-2','us-west-1','us-west-2',
    'eu-west-1','eu-west-2','eu-west-3','eu-central-1',
    'ap-southeast-1','ap-southeast-2','ap-northeast-1','ap-south-1',
    'ca-central-1','sa-east-1',
  ];

  if (loading) return <div style={{ paddingTop: 60, textAlign: 'center' }}><Spin size="large" /></div>;

  return (
    <div style={{ maxWidth: 640, width: '100%' }}>
      <SesGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />
      <Card style={{ borderRadius: 'var(--r-lg)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, background: 'var(--warn-bg)', borderRadius: 'var(--r-md)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CloudOutlined style={{ color: 'var(--warn)', fontSize: 20 }} />
            </div>
            <div>
              <Text strong style={{ fontSize: 15, display: 'block', color: 'var(--text)' }}>AWS SES Configuration</Text>
              <Text style={{ color: 'var(--text-2)', fontSize: 13 }}>Credentials for sending emails via Amazon Simple Email Service</Text>
            </div>
          </div>
          <Button
            icon={<InfoCircleOutlined />}
            onClick={() => setGuideOpen(true)}
            style={{ borderRadius: 'var(--r-md)', borderColor: 'var(--warn)', color: 'var(--warn)', fontWeight: 600 }}
          >
            Setup Guide
          </Button>
        </div>

        {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} style={{ marginBottom: 16, borderRadius: 'var(--r-md)' }} />}

        <Form form={form} layout="vertical" onFinish={handleSave}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0 16px' }}>
            <Form.Item
              name="ses_access_key_id"
              label="AWS Access Key ID"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Input
                prefix={<ApiOutlined style={{ color: themeToken.colorTextTertiary }} />}
                placeholder="AKIA…"
                style={{ borderRadius: 10, fontFamily: 'monospace' }}
              />
            </Form.Item>

            <Form.Item
              name="ses_secret_access_key"
              label="AWS Secret Access Key"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: themeToken.colorTextTertiary }} />}
                placeholder="Existing key masked — enter new to change"
                style={{ borderRadius: 10, fontFamily: 'monospace' }}
              />
            </Form.Item>
          </div>

          <Form.Item
            name="ses_region"
            label="AWS Region"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Select
              showSearch
              placeholder="Select or type a region…"
              style={{ fontFamily: 'monospace' }}
              filterOption={(input, option) =>
                option.value.toLowerCase().includes(input.toLowerCase())
              }
              options={AWS_REGIONS.map(r => ({ label: r, value: r }))}
            />
          </Form.Item>

          <Divider style={{ margin: '8px 0 20px' }} />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0 16px' }}>
            <Form.Item
              name="ses_from_email"
              label="From Email Address"
              rules={[{ required: true, type: 'email', message: 'Enter a valid verified email' }]}
              extra="Must be verified in AWS SES"
            >
              <Input
                prefix={<MailOutlined style={{ color: themeToken.colorTextTertiary }} />}
                placeholder="noreply@yourdomain.com"
                style={{ borderRadius: 10 }}
              />
            </Form.Item>

            <Form.Item
              name="ses_from_name"
              label="From Name"
            >
              <Input
                prefix={<UserOutlined style={{ color: themeToken.colorTextTertiary }} />}
                placeholder="SES Mailbox"
                style={{ borderRadius: 10 }}
              />
            </Form.Item>
          </div>

          <Divider style={{ margin: '8px 0 20px' }} />

          <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving} style={{ borderRadius: 'var(--r-md)' }}>
              Save Settings
            </Button>
            <Button icon={<ReloadOutlined />} loading={testing} onClick={handleTest} style={{ borderRadius: 'var(--r-md)' }}>
              Test Connection
            </Button>
          </div>
        </Form>
      </Card>

      {/* Test result */}
      {testResult && (
        <Alert
          type={testResult.ok ? 'success' : 'error'}
          showIcon
          icon={testResult.ok ? <CheckCircleOutlined /> : undefined}
          message={testResult.ok ? 'Connection successful' : 'Connection failed'}
          description={
            testResult.ok ? (
              <div style={{ marginTop: 4 }}>
                <Text style={{ fontSize: 13 }}>Region: <strong style={{ fontFamily: 'var(--font-mono)' }}>{testResult.region}</strong></Text><br />
                {testResult.quota && (
                  <>
                    <Text style={{ fontSize: 13 }}>Daily send limit: <strong style={{ fontFamily: 'var(--font-mono)' }}>{testResult.quota.max24HourSend}</strong></Text><br />
                    <Text style={{ fontSize: 13 }}>Sent last 24 h: <strong style={{ fontFamily: 'var(--font-mono)' }}>{testResult.quota.sentLast24Hours}</strong></Text><br />
                    <Text style={{ fontSize: 13 }}>Max send rate: <strong style={{ fontFamily: 'var(--font-mono)' }}>{testResult.quota.maxSendRate} emails/sec</strong></Text>
                  </>
                )}
              </div>
            ) : testResult.message
          }
          closable
          onClose={() => setTestResult(null)}
          style={{ borderRadius: 'var(--r-md)' }}
        />
      )}

      {/* ── Sender Identity Status ── */}
      {identityStatus?.configured && identityStatus?.email && (() => {
        const { status, email } = identityStatus;
        const cfg = {
          Success:     { icon: <CheckCircleOutlined />,       badge: 'Verified',             color: 'var(--ok)',     bg: 'var(--ok-bg)',     desc: `${email} is verified in AWS SES. You're ready to send.` },
          Pending:     { icon: <ClockCircleOutlined />,       badge: 'Pending Verification', color: 'var(--warn)',   bg: 'var(--warn-bg)',   desc: `AWS sent a verification email to ${email}. Click the link inside it, then refresh status.` },
          Failed:      { icon: <ExclamationCircleOutlined />, badge: 'Verification Failed',  color: 'var(--danger)', bg: 'var(--danger-bg)', desc: `Verification failed for ${email}. Re-send the verification email to try again.` },
          NotStarted:  { icon: <ExclamationCircleOutlined />, badge: 'Not Verified',         color: 'var(--danger)', bg: 'var(--danger-bg)', desc: `${email} has not been verified in AWS SES. Emails will be rejected until it is verified.` },
          NotConfigured: null,
        }[status];
        if (!cfg) return null;
        return (
          <Card
            style={{
              borderRadius: 'var(--r-md)', marginTop: 12,
              border: '1px solid var(--border)',
              background: cfg.bg,
              boxShadow: 'none',
            }}
            styles={{ body: { padding: '14px 18px' } }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                {React.cloneElement(cfg.icon, { style: { fontSize: 18, color: cfg.color, marginTop: 1 } })}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                    <Text strong style={{ fontSize: 13, color: 'var(--text)' }}>Sender Identity</Text>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '1px 8px', borderRadius: 20,
                      background: 'transparent', color: cfg.color,
                      border: `1px solid ${cfg.color}`,
                    }}>
                      {cfg.badge}
                    </span>
                  </div>
                  <Text style={{ fontSize: 12, color: 'var(--text-2)' }}>{cfg.desc}</Text>
                </div>
              </div>
              <Space size={8}>
                {(status === 'Pending') && (
                  <Button
                    size="small" icon={<SyncOutlined spin={identityLoading} />}
                    onClick={loadIdentityStatus}
                    loading={identityLoading}
                    style={{ borderRadius: 'var(--r-md)', borderColor: cfg.color, color: cfg.color }}
                  >
                    Refresh Status
                  </Button>
                )}
                {(status === 'NotStarted' || status === 'Failed') && (
                  <Button
                    size="small" type="primary" icon={<MailOutlined />}
                    loading={verifying}
                    onClick={handleVerifyIdentity}
                    style={{ borderRadius: 'var(--r-md)' }}
                  >
                    Send Verification Email
                  </Button>
                )}
              </Space>
            </div>
          </Card>
        );
      })()}

      {/* ── From-Email Addresses ── */}
      <FromEmailsCard themeToken={themeToken} />

      {/* Info box */}
      <Card style={{ borderRadius: 'var(--r-lg)', border: '1px solid var(--info)', background: 'var(--info-bg)', boxShadow: 'none', marginTop: 16 }}>
        <Text strong style={{ color: 'var(--info)', display: 'block', marginBottom: 8 }}>Getting started with AWS SES</Text>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[
            '1. Create an IAM user with AmazonSESFullAccess permission in AWS Console',
            '2. Generate Access Key ID + Secret Access Key for that IAM user',
            '3. Verify your sender email address (or domain) in the SES console',
            '4. If your account is in SES sandbox, verify recipient emails too or request production access',
          ].map(step => (
            <Text key={step} style={{ color: 'var(--info)', fontSize: 13 }}>{step}</Text>
          ))}
        </div>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────
// ─────────────────────────────────────────────
// Tracking Domain tab
// ─────────────────────────────────────────────
const TrackingDomainTab = () => {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [domain, setDomain]       = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const r = await axios.get('/api/settings/tracking-domain');
      setData(r.data);
      setDomain(r.data.tracking_domain || '');
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put('/api/settings/tracking-domain', { tracking_domain: domain.trim() });
      message.success('Tracking domain saved');
      await load();
    } catch (e) {
      message.error(e.response?.data?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleVerify = async () => {
    try {
      setVerifying(true);
      const r = await axios.post('/api/settings/tracking-domain/verify');
      if (r.data.verified) {
        message.success(r.data.message);
      } else {
        message.warning(r.data.message);
      }
      await load();
    } catch (e) {
      message.error(e.response?.data?.message || 'Verification failed');
    } finally { setVerifying(false); }
  };

  const cnameTarget = data?.cname_target || 'api.sesmailbox.com';
  const isVerified  = data?.tracking_domain_verified;
  const hasDomain   = !!data?.tracking_domain;

  const cardStyle = {
    borderRadius: 'var(--r-lg)',
    border: '1px solid var(--border)',
    marginBottom: 20,
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;

  return (
    <div style={{ maxWidth: 720, width: '100%' }}>

      {/* Explanation card */}
      <Card style={cardStyle}>
        <Space align="start" style={{ marginBottom: 16 }}>
          <GlobalOutlined style={{ fontSize: 22, color: '#0a40d9', marginTop: 2 }} />
          <div>
            <Text strong style={{ fontSize: 15 }}>Why set a custom tracking domain?</Text>
            <div style={{ color: 'var(--text-2)', marginTop: 6, lineHeight: 1.7 }}>
              By default, all tracking pixels, click-redirect links, and unsubscribe URLs in your campaign
              emails contain <Text code>sesmailbox.com</Text>. Spam filters see this domain across thousands
              of other senders — which can hurt your deliverability.<br /><br />
              When you configure your own subdomain (e.g. <Text code>track.yourcompany.com</Text>), every
              link in your campaigns uses <em>your</em> domain. Your sending reputation stays completely
              isolated from other SES Mailbox customers.
            </div>
          </div>
        </Space>
      </Card>

      {/* DNS setup instructions */}
      <Card title={<Space><CloudServerOutlined />Step 1 — Add a CNAME record</Space>} style={cardStyle}>
        <Text style={{ color: 'var(--text-2)', display: 'block', marginBottom: 16 }}>
          In your DNS provider (Cloudflare, Route 53, GoDaddy, etc.) add the following record:
        </Text>
        <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '14px 18px', fontFamily: 'monospace', fontSize: 13, marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
            <div><Text type="secondary">Type</Text><br /><Text strong>CNAME</Text></div>
            <div><Text type="secondary">Host / Name</Text><br /><Text strong>track</Text>&nbsp;<Text type="secondary">(or any subdomain)</Text></div>
            <div><Text type="secondary">Value / Points to</Text><br /><Text strong copyable>{cnameTarget}</Text></div>
            <div><Text type="secondary">TTL</Text><br /><Text strong>Auto / 300</Text></div>
          </div>
        </div>
        <Alert
          type="info"
          showIcon
          message="DNS changes can take up to 48 hours to propagate globally. Usually it's under 5 minutes with Cloudflare."
        />
      </Card>

      {/* Domain input */}
      <Card title={<Space><EditOutlined />Step 2 — Enter your tracking subdomain</Space>} style={cardStyle}>
        <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
          <Input
            placeholder="track.yourcompany.com"
            value={domain}
            onChange={e => setDomain(e.target.value.toLowerCase().replace(/\s/g, '').replace(/^https?:\/\//, ''))}
            prefix={<GlobalOutlined />}
            style={{ flex: 1 }}
          />
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
          >
            Save
          </Button>
        </Space.Compact>

        {hasDomain && (
          <Space style={{ marginBottom: 12 }}>
            <Text>Current domain:</Text>
            <Text code>{data.tracking_domain}</Text>
            {isVerified
              ? <Tag icon={<CheckCircleOutlined />} color="success">Verified</Tag>
              : <Tag icon={<ClockCircleOutlined />} color="warning">Pending verification</Tag>}
          </Space>
        )}

        {hasDomain && !isVerified && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Domain saved but not yet verified"
            description={`We have not confirmed that ${data.tracking_domain} CNAMEs to ${cnameTarget} yet. Tracking URLs will still use sesmailbox.com until verification passes.`}
          />
        )}

        {isVerified && (
          <Alert
            type="success"
            showIcon
            message={`✅ ${data.tracking_domain} is active`}
            description="All new campaign emails will use your domain for tracking pixels, click links, and unsubscribe URLs."
          />
        )}
      </Card>

      {/* Verify step */}
      {hasDomain && (
        <Card
          title={<Space><CheckCircleOutlined />{isVerified ? 'DNS Verification' : 'Step 3 — Verify DNS'}</Space>}
          style={cardStyle}
        >
          <Text style={{ color: 'var(--text-2)', display: 'block', marginBottom: 16 }}>
            {isVerified
              ? 'Your domain is verified. Click below to re-check if you updated your DNS records.'
              : 'Once you have added the CNAME, click the button below. We will perform a live DNS lookup to confirm the record is in place.'}
          </Text>
          <Button
            type={isVerified ? 'default' : 'primary'}
            icon={<SyncOutlined />}
            loading={verifying}
            onClick={handleVerify}
          >
            {isVerified ? 'Re-verify DNS' : 'Verify DNS now'}
          </Button>
        </Card>
      )}

      {/* What changes section */}
      <Card title={<Space><InfoCircleOutlined />What uses your tracking domain?</Space>} style={cardStyle}>
        <AntList
          size="small"
          dataSource={[
            { icon: <EyeOutlined />,         color: '#0a40d9', title: 'Open tracking pixel', desc: 'The invisible 1×1 image injected into campaign HTML.' },
            { icon: <LinkOutlined />,        color: '#0ea5e9', title: 'Click tracking redirects', desc: 'Every link in your campaigns is wrapped through your domain.' },
            { icon: <StopOutlined />,        color: '#f59e0b', title: 'Unsubscribe footer link', desc: 'The "Unsubscribe" link visible to recipients in every email.' },
            { icon: <ThunderboltOutlined />, color: '#10b981', title: 'List-Unsubscribe header', desc: 'The RFC 8058 one-click unsubscribe used by Gmail, Apple Mail, Outlook.' },
          ]}
          renderItem={item => (
            <AntList.Item>
              <Space align="start">
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 36, height: 36, borderRadius: 9, flexShrink: 0,
                  background: `${item.color}1a`, color: item.color, fontSize: 17,
                }}>
                  {item.icon}
                </span>
                <div>
                  <Text strong>{item.title}</Text>
                  <div style={{ color: 'var(--text-2)', fontSize: 13 }}>{item.desc}</div>
                </div>
              </Space>
            </AntList.Item>
          )}
        />
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────
// Billing tab — business profile, subscription, invoices
// ─────────────────────────────────────────────
const CANCEL_REASONS = [
  'Too expensive',
  'Missing features I need',
  'Switching to another tool',
  'Not sending emails anymore',
  'Technical issues',
  'Just testing / evaluating',
  'Other',
];

const BillingTab = () => {
  const [profileForm] = Form.useForm();
  const [profile,       setProfile]       = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving,  setProfileSaving]  = useState(false);

  const [billingStatus, setBillingStatus] = useState(null);
  const [invoices,      setInvoices]      = useState([]);
  const [upcoming,      setUpcoming]      = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);

  const [cancelModal,  setCancelModal]  = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelOther,  setCancelOther]  = useState('');
  const [canceling,    setCanceling]    = useState(false);

  const [portaling,    setPortaling]    = useState(false);

  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const IS_INDIA    = (() => { try { const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; return tz === 'Asia/Kolkata' || tz === 'Asia/Calcutta'; } catch { return false; } })();
  const PRICE_LABEL = IS_INDIA ? '₹999 / month' : '$9.99 / month';
  const PH = IS_INDIA
    ? { addr1: '123 MG Road', city: 'Mumbai', state: 'Maharashtra', postal: '400001', country: 'India',         tax: 'GSTIN (optional)' }
    : { addr1: '123 Market St', city: 'San Francisco', state: 'California', postal: '94105', country: 'United States', tax: 'EIN / VAT (optional)' };

  const fmtAmt = (amount, currency = 'inr') => {
    const locale = currency.toLowerCase() === 'inr' ? 'en-IN' : 'en-US';
    return new Intl.NumberFormat(locale, { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100);
  };

  const loadAll = async () => {
    try {
      const [profileRes, statusRes, invRes, upRes] = await Promise.allSettled([
        axios.get('/api/settings/billing-profile', { headers }),
        axios.get('/api/billing/status',           { headers }),
        axios.get('/api/billing/invoices',         { headers }),
        axios.get('/api/billing/upcoming-invoice', { headers }),
      ]);
      if (profileRes.status === 'fulfilled') {
        setProfile(profileRes.value.data);
        profileForm.setFieldsValue(profileRes.value.data);
      }
      if (statusRes.status === 'fulfilled') setBillingStatus(statusRes.value.data);
      if (invRes.status === 'fulfilled')    setInvoices(invRes.value.data?.invoices || []);
      if (upRes.status === 'fulfilled')     setUpcoming(upRes.value.data?.upcoming);
    } finally {
      setProfileLoading(false);
      setStatusLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const handleProfileSave = async (values) => {
    setProfileSaving(true);
    try {
      await axios.put('/api/settings/billing-profile', values, { headers });
      message.success('Billing profile saved');
    } catch (e) {
      message.error(e.response?.data?.message || 'Failed to save');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleCancel = async () => {
    const reason = cancelReason === 'Other' ? cancelOther.trim() : cancelReason;
    if (!reason) { message.warning('Please select a reason'); return; }
    setCanceling(true);
    try {
      await axios.post('/api/billing/cancel', { reason }, { headers });
      message.success('Subscription cancelled — access continues until the end of your billing period.');
      setCancelModal(false);
      await loadAll();
    } catch (e) {
      message.error(e.response?.data?.message || 'Failed to cancel');
    } finally {
      setCanceling(false);
    }
  };

  const handlePortal = async () => {
    setPortaling(true);
    try {
      const res = await axios.post('/api/billing/portal', {}, { headers });
      window.open(res.data.url, '_blank');
    } catch (e) {
      message.error('Could not open payment portal');
    } finally {
      setPortaling(false);
    }
  };

  const cardStyle = { borderRadius: 'var(--r-lg)', border: '1px solid var(--border)', marginBottom: 20 };
  const isPro     = billingStatus?.plan === 'pro';
  const isCancelled = billingStatus?.plan_status === 'cancelled';

  const invoiceCols = [
    { title: 'Date',        dataIndex: 'created_at', key: 'date',   width: 120,
      render: d => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) },
    { title: 'Description', dataIndex: 'description', key: 'desc', ellipsis: true },
    { title: 'Amount',      key: 'amount', width: 110,
      render: (_, r) => fmtAmt(r.amount_cents || r.amount || 0, r.currency || 'usd') },
    { title: 'Status',      dataIndex: 'status', key: 'status', width: 90,
      render: s => <Tag color={s === 'paid' ? 'green' : s === 'open' ? 'orange' : 'default'}>{(s || 'paid').toUpperCase()}</Tag> },
  ];

  return (
    <div style={{ maxWidth: 720, width: '100%' }}>

      {/* ── Subscription ── */}
      <Card title={<Space><CrownOutlined />Subscription</Space>} style={cardStyle}>
        {statusLoading ? <Spin /> : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 20 }}>{isPro ? 'Pro Plan' : 'Free Plan'}</span>
                  <Tag color={isPro ? (isCancelled ? 'orange' : 'green') : 'default'}>
                    {isPro ? (isCancelled ? 'Cancels at period end' : 'Active') : 'Free Tier'}
                  </Tag>
                </div>
                <Text style={{ color: 'var(--text-3)', fontSize: 13 }}>
                  {isPro ? PRICE_LABEL : 'No charge'}
                </Text>
              </div>
              {isPro && (
                <Space wrap>
                  <Button icon={<CreditCardOutlined />} onClick={handlePortal} loading={portaling}>
                    Manage Payment Method
                  </Button>
                  {!isCancelled && (
                    <Button danger onClick={() => setCancelModal(true)}>
                      Cancel Subscription
                    </Button>
                  )}
                </Space>
              )}
            </div>

            {upcoming && (
              <Alert
                type="info" showIcon
                message={`Next charge: ${fmtAmt(upcoming.amount_due, upcoming.currency)} on ${new Date(upcoming.next_date * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`}
                style={{ marginBottom: 0 }}
              />
            )}
            {isCancelled && (
              <Alert
                type="warning" showIcon
                message="Your subscription is cancelled. Pro access continues until the end of the current billing period."
                style={{ marginTop: 12 }}
              />
            )}
          </>
        )}
      </Card>

      {/* ── Business / Billing Profile ── */}
      <Card title={<Space><BankOutlined />Business &amp; Billing Profile</Space>} style={cardStyle}>
        <Text style={{ color: 'var(--text-3)', fontSize: 13, display: 'block', marginBottom: 20 }}>
          This information appears on your invoices and receipts.
        </Text>
        {profileLoading ? <Spin /> : (
          <Form form={profileForm} layout="vertical" onFinish={handleProfileSave}>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="billing_name" label="Business / Legal Name">
                  <Input placeholder="Acme Corp Ltd." />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="billing_email" label="Billing Email"
                  rules={[{ type: 'email', message: 'Enter a valid email' }]}>
                  <Input placeholder="billing@yourcompany.com" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="billing_address1" label="Address Line 1">
              <Input placeholder={PH.addr1} />
            </Form.Item>
            <Form.Item name="billing_address2" label="Address Line 2 (optional)">
              <Input placeholder="Suite 100, Floor 3" />
            </Form.Item>
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item name="billing_city" label="City">
                  <Input placeholder={PH.city} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="billing_state" label="State / Province">
                  <Input placeholder={PH.state} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="billing_postal_code" label="Postal Code">
                  <Input placeholder={PH.postal} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="billing_country" label="Country">
                  <Input placeholder={PH.country} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="billing_tax_id" label="Tax ID / VAT Number (optional)">
                  <Input placeholder={PH.tax} />
                </Form.Item>
              </Col>
            </Row>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={profileSaving}>
              Save Billing Profile
            </Button>
          </Form>
        )}
      </Card>

      {/* ── Invoices ── */}
      <Card title={<Space><FileTextOutlined />Invoices</Space>} style={cardStyle}>
        {invoices.length === 0 ? (
          <Empty description="No invoices yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Table
            dataSource={invoices}
            columns={invoiceCols}
            rowKey="id"
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
            size="small"
            scroll={{ x: 'max-content' }}
          />
        )}
      </Card>

      {/* ── Cancel Modal ── */}
      <Modal
        title="Cancel Subscription"
        open={cancelModal}
        onCancel={() => { setCancelModal(false); setCancelReason(''); setCancelOther(''); }}
        footer={null}
        width={480}
      >
        <Text style={{ color: 'var(--text-2)', display: 'block', marginBottom: 20 }}>
          We're sorry to see you go. Your Pro access will continue until the end of the current billing period.
        </Text>
        <Text strong style={{ display: 'block', marginBottom: 12 }}>Why are you cancelling?</Text>
        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
          {CANCEL_REASONS.map(r => (
            <div
              key={r}
              onClick={() => setCancelReason(r)}
              style={{
                padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                border: `1px solid ${cancelReason === r ? 'var(--brand-primary)' : 'var(--border)'}`,
                background: cancelReason === r ? 'var(--brand-tint)' : 'transparent',
                color: cancelReason === r ? 'var(--brand-primary)' : 'var(--text)',
                fontWeight: cancelReason === r ? 600 : 400,
                transition: 'all .15s',
              }}
            >
              {r}
            </div>
          ))}
        </Space>
        {cancelReason === 'Other' && (
          <Input.TextArea
            rows={3}
            placeholder="Tell us more (optional but very helpful)..."
            value={cancelOther}
            onChange={e => setCancelOther(e.target.value)}
            style={{ marginBottom: 16 }}
          />
        )}
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={() => { setCancelModal(false); setCancelReason(''); setCancelOther(''); }}>
            Keep my subscription
          </Button>
          <Button
            danger loading={canceling}
            disabled={!cancelReason}
            onClick={handleCancel}
          >
            Confirm cancellation
          </Button>
        </Space>
      </Modal>
    </div>
  );
};

// ─────────────────────────────────────────────
// Main Settings page
// ─────────────────────────────────────────────
const VALID_TABS = ['profile', 'billing', 'users', 'ses', 'tracking'];

const Settings = () => {
  const location = useLocation();
  const hashTab = location.hash.replace('#', '');
  const [activeTab, setActiveTab] = useState(
    VALID_TABS.includes(hashTab) ? hashTab : 'profile'
  );

  // Sync if user navigates to a new hash (e.g. back/forward)
  useEffect(() => {
    const tab = location.hash.replace('#', '');
    if (VALID_TABS.includes(tab)) setActiveTab(tab);
  }, [location.hash]);

  const tabItems = [
    {
      key:      'profile',
      label:    <Space><UserOutlined />Profile</Space>,
      children: <ProfileTab />,
    },
    {
      key:      'billing',
      label:    <Space><CrownOutlined />Billing</Space>,
      children: <BillingTab />,
    },
    {
      key:      'users',
      label:    <Space><UserOutlined />Users</Space>,
      children: <UsersTab />,
    },
    {
      key:      'ses',
      label:    <Space><CloudOutlined />Email (SES)</Space>,
      children: <SesTab />,
    },
    {
      key:      'tracking',
      label:    <Space><GlobalOutlined />Tracking Domain</Space>,
      children: <TrackingDomainTab />,
    },
  ];

  return (
    <div>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        style={{ background: 'transparent' }}
      />
    </div>
  );
};

export default Settings;
